import { getToken, clearToken, isAuthDisabled, redirectToLogin, isDevLogin, redirectToSystemAdmin } from '../auth/auth';
import { getLocalEmployeeId } from '../auth/permissions';

const API_BASE_URL =
	process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1/dashboard';

function extractErrorMessage(payload, fallbackMessage) {
	if (!payload || typeof payload !== 'object') {
		return fallbackMessage;
	}

	if (typeof payload.message === 'string' && payload.message.trim()) {
		return payload.message;
	}

	if (typeof payload.detail === 'string' && payload.detail.trim()) {
		return payload.detail;
	}

	if (payload.errors) {
		if (Array.isArray(payload.errors)) {
			return payload.errors.join('\n');
		}

		if (typeof payload.errors === 'object') {
			const flattened = Object.values(payload.errors)
				.flat()
				.filter(Boolean)
				.map((value) => (typeof value === 'string' ? value : JSON.stringify(value)));
			if (flattened.length > 0) {
				return flattened.join('\n');
			}
		}
	}

	// DRF のバリデーションエラーは {"項目": ["理由", ...]} や ["理由"] の生の形で返る。
	// そのままだと JSON 文字列が画面に出てしまうため、理由だけを取り出す。
	const validationMessages = (Array.isArray(payload) ? payload : Object.values(payload))
		.flat()
		.filter((value) => typeof value === 'string' && value.trim());
	if (validationMessages.length > 0) {
		return validationMessages.join('\n');
	}

	return fallbackMessage;
}

export async function apiFetchAbsolute(url, options = {}) {
	const headers = {
		'Content-Type': 'application/json',
		...(options.headers || {}),
	};

	if (!isAuthDisabled()) {
		const token = getToken();
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}
	} else {
		// 認証無効環境（ローカル・検証）はトークンが無いため、
		// ユーザー選択画面で認証済みの社員番号を送って個人を識別させる。
		const employeeId = getLocalEmployeeId();
		if (employeeId) {
			headers['X-Local-User'] = employeeId;
		}
	}

	const response = await fetch(url, {
		credentials: 'include',
		headers,
		...options,
	});

	if (response.status === 401 && !isAuthDisabled()) {
		// 開発者ログイン中は MS ではなく開発者ログイン画面へ戻す（clearToken は dev フラグを消さない）
		const wasDevLogin = isDevLogin();
		clearToken();
		if (wasDevLogin) {
			redirectToSystemAdmin();
		} else {
			redirectToLogin();
		}
		throw new Error('Authentication required');
	}

	if (!response.ok) {
		const text = await response.text();
		const fallbackMessage = text || `HTTP ${response.status}`;
		let payload = null;
		try {
			payload = text ? JSON.parse(text) : null;
		} catch (parseError) {
			payload = null;
		}
		const err = new Error(extractErrorMessage(payload, fallbackMessage));
		err.status = response.status;
		throw err;
	}

	if (response.status === 204) {
		return null;
	}

	const responseText = await response.text();
	if (!responseText) {
		return null;
	}

	return JSON.parse(responseText);
}

// In-flight GET request deduplication cache.
// If the same GET URL is requested while a previous request is still pending,
// return the same promise to avoid duplicate network calls
// (e.g. React StrictMode double-mount).
const _inflight = new Map();

export async function apiFetch(path, options = {}) {
	const url = `${API_BASE_URL}${path}`;
	const method = (options.method || 'GET').toUpperCase();

	if (method !== 'GET') {
		return apiFetchAbsolute(url, options);
	}

	if (_inflight.has(url)) {
		return _inflight.get(url);
	}

	const promise = apiFetchAbsolute(url, options).finally(() => {
		_inflight.delete(url);
	});
	_inflight.set(url, promise);
	return promise;
}
